import React, { useEffect, useRef } from 'react'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { AIPlayerSenderType } from '@/constants/chat'
import { setError } from '@/redux/slices/aiPlayerChatSlice'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import OGEAvatar from '@/components/OGE/OGEAvatar'
import { formatDateRelative, formatTime } from '@/utils/dateTimeUtils'
import { cn } from '@/lib/utils'
const AIPlayerMessages: React.FC = () => {
  const dispatch = useAppDispatch()
  const messages = useAppSelector((state) => state.aiPlayerChat.messages)
  const error = useAppSelector((state) => state.aiPlayerChat.error)
  const connected = useAppSelector((state) => state.aiPlayerChat.connected)
  const teamAssignment = useAppSelector(
    (state) => state.aiPlayerChat.teamAssignment
  )
  const messagesEndRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const botDisplayName = teamAssignment?.botName?.trim() || 'AI Player'
  const teamSubtitle = teamAssignment?.teamName?.trim() || ''

  return (
    <div className='flex flex-col flex-1 min-h-0 h-full w-full'>
      {!connected && (
        <Alert className='mx-3 mt-3 border-amber-200 bg-amber-50 shrink-0'>
          <AlertDescription className='text-sm text-amber-900'>
            Connecting to AI Player chat… Messages may not send until connected.
          </AlertDescription>
        </Alert>
      )}
      {error && (
        <Alert variant='destructive' className='relative mx-3 mt-3 pr-10 shrink-0'>
          <AlertDescription className='text-sm'>{error}</AlertDescription>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            className='absolute top-2 right-2 h-7'
            onClick={() => dispatch(setError(null))}
          >
            Dismiss
          </Button>
        </Alert>
      )}
      <div className='overflow-auto flex-1 [&::-webkit-scrollbar]:hidden'>
        {messages.length === 0 ? (
          <div className='flex justify-center items-center h-full text-gray-500'>
            <p>No messages yet. Start the conversation!</p>
          </div>
        ) : (
          messages.map((message, idx) => {
            const isPlayer = message.sender_type === AIPlayerSenderType.Player
            const isRightAligned = isPlayer
            const showDate =
              idx === 0 ||
              (message.created_at &&
                messages[idx - 1]?.created_at &&
                formatDateRelative(message.created_at) !==
                  formatDateRelative(messages[idx - 1].created_at!))

            return (
              <div key={message.id} className='px-3'>
                {showDate && message.created_at && (
                  <div className='flex justify-center items-center my-4'>
                    <div className='px-3 py-1 text-xs font-medium text-gray-600 bg-gray-200 rounded-full'>
                      {formatDateRelative(message.created_at)}
                    </div>
                  </div>
                )}

                <div
                  className={cn(
                    'flex mb-4',
                    isRightAligned ? 'justify-end' : 'justify-start'
                  )}
                >
                  <div
                    className={cn(
                      'max-w-[80%] p-4 rounded-[20px] bg-[#E6ECF1]',
                      isRightAligned ? 'text-right' : 'text-left'
                    )}
                  >
                    {!isRightAligned && (
                      <div className='flex gap-3 items-center mb-2'>
                        <OGEAvatar
                          src={null}
                          fallback={botDisplayName.substring(0, 2).toUpperCase()}
                          variant='sm'
                          isLoading={false}
                        />
                        <div className='flex flex-col'>
                          <span className='text-base font-medium text-dark-blue'>
                            {botDisplayName}
                          </span>
                          {teamSubtitle && (
                            <span className='text-xs text-gray-500'>
                              {teamSubtitle}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    <p className='text-sm font-medium text-black whitespace-break-spaces'>
                      {message.content}
                    </p>

                    {message.created_at && (
                      <div className='flex justify-end items-center gap-2 mt-2'>
                        <span className='text-xs text-gray-500 opacity-75'>
                          {formatTime(message.created_at)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )
          })
        )}
        <div ref={messagesEndRef} />
      </div>
    </div>
  )
}

export default AIPlayerMessages
